import { RubyGemMark } from '../../components/RubyGemMark';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, FolderInput, FolderPlus, Plus, RefreshCw, ScrollText,
} from 'lucide-react';
import {
  Button as UIButton, FieldLabel, IconX, Tag as UITag,
} from '@documents-ui';
import type { DocumentKind, Folder, LegalDocument } from '@platform/shared-types';
import { StaffPage } from '../../components/staff/StaffChrome';
import LegalNav from './LegalNav';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import { categoryLabel, useDocumentCategories } from './documentCategories';
import { Explainer } from './rubyChrome';
import MoveDocumentModal from './MoveDocumentModal';
import type { DocumentCategory } from '@platform/shared-types';
import { documentsApi } from '../../services/documentsApi';
import SystemDocumentsPanel from './SystemDocumentsPanel';
import type { DocumentDetail } from '../../services/documentsApi';
import {
  activeVersionRow, documentHeadState, formatUtcDay, headVersion, isValidSlug, pendingVersion,
  slugify,
} from './legalFormat';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * LEGAL / POLICIES — mockup screen 1, in the RUBY light chrome (StaffPage:
 * black header bar + ruby underline + stone-100 content with white cards).
 *
 * Three stat tiles, the folder tree, and the document table
 * (Document · Status · Active · Effective · Pending).
 *
 * WHY THIS PAGE MAKES N+1 REQUESTS. `GET /admin/documents` returns the stored
 * document rows ONLY — no `activeVersionNo`, no version chain, none of the
 * derived booleans. Status, Active and Effective all come from the chain, so
 * each row's detail is fetched from `GET /admin/documents/{id}` (bounded
 * concurrency below). The right fix is for the list endpoint to project those
 * fields server-side; until it does, this is the only honest way to fill the
 * columns the approved design asks for. It is NOT resolved by caching a
 * "status" on the document row — status is derived, never stored.
 */

const POOL = 6;

/** Small bounded-concurrency map so a long folder does not open 40 sockets. */
async function mapPool<T, R>(items: readonly T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const i = cursor;
      cursor += 1;
      if (i >= items.length) return;
      out[i] = await fn(items[i]);
    }
  });
  await Promise.all(workers);
  return out;
}

/* ── RUBY light-chrome adapters over the shared @documents-ui primitives ──
 *
 * RESTYLE ONLY: same props, same behaviour. The shared package still ships its
 * dark defaults, so these thin wrappers repaint them with `!` overrides for
 * this light page. If/when @documents-ui itself goes light these become no-ops
 * with identical class values. */

const BTN_LIGHT: Record<string, string> = {
  default: '!border-stone-300 !bg-white !text-stone-700 hover:!bg-stone-50',
  primary: '!border-admin-primary !bg-admin-primary !text-white hover:!border-admin-secondary hover:!bg-admin-secondary',
  admin: '!border-admin-primary !bg-admin-primary !text-white hover:!border-admin-secondary hover:!bg-admin-secondary',
  danger: '!border-red-600 !bg-red-600 !text-white hover:!border-red-700 hover:!bg-red-700',
};

function Button({ className = '', ...rest }: ComponentProps<typeof UIButton>) {
  return <UIButton {...rest} className={`${BTN_LIGHT[rest.variant ?? 'default']} ${className}`} />;
}

const TAG_LIGHT: Record<string, string> = {
  published: '!bg-emerald-100 !text-emerald-800',
  active: '!bg-emerald-100 !text-emerald-800',
  draft: '!bg-stone-100 !text-stone-600',
  minor: '!bg-stone-100 !text-stone-600',
  review: '!bg-amber-100 !text-amber-800',
  approved: '!bg-blue-100 !text-blue-800',
  scheduled: '!bg-rose-100 !text-rose-800',
  retired: '!bg-red-100 !text-red-800',
  material: '!bg-red-100 !text-red-800',
};

function Tag({ className = '', ...rest }: ComponentProps<typeof UITag>) {
  return <UITag {...rest} className={`${TAG_LIGHT[rest.tone] ?? ''} ${className}`} />;
}


const INPUT_CLASS =
  'w-full min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-[13px] text-stone-900 placeholder:text-stone-400 focus:border-admin-primary focus:outline-none focus:ring-1 focus:ring-admin-primary';

/** Light modal card — same structure and behaviour as @documents-ui ModalShell. */
function ModalShell({
  title, subtitle, children, footerNote, footerActions, onClose, labelledById,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footerNote?: ReactNode;
  footerActions?: ReactNode;
  onClose: () => void;
  labelledById?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        className="w-full max-w-[620px] rounded-2xl border border-stone-200 bg-white shadow-xl"
      >
        <div className="flex items-start gap-3 border-b border-stone-200 px-5 py-4">
          <div className="flex-1">
            <h3 id={labelledById} className="text-base font-semibold text-stone-900">{title}</h3>
            {subtitle && <p className="mt-1 text-[12.5px] text-stone-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700"
          >
            <IconX size={18} />
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
        {(footerNote || footerActions) && (
          <div className="flex flex-col gap-3 border-t border-stone-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[11.5px] text-stone-500">{footerNote}</span>
            <div className="flex gap-2">{footerActions}</div>
          </div>
        )}
      </div>
    </div>
  );
}

interface TreeNode {
  folder: Folder;
  children: TreeNode[];
}

function buildTree(folders: readonly Folder[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  folders.forEach((f) => byId.set(f.folderId, { folder: f, children: [] }));
  const roots: TreeNode[] = [];
  byId.forEach((node) => {
    const parentId = node.folder.parentFolderId;
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent) parent.children.push(node); else roots.push(node);
  });
  return roots;
}

function StatTile({ value, label, tone }: { value: string; label: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className={`text-2xl font-bold tracking-tight ${tone || 'text-stone-900'}`}>{value}</div>
      <div className="mt-1 text-[11.5px] text-stone-500">{label}</div>
    </div>
  );
}

export default function PoliciesList() {
  const navigate = useNavigate();

  const [folders, setFolders] = useState<Folder[]>([]);
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [details, setDetails] = useState<Record<string, DocumentDetail>>({});
  /** documentId → acceptances that go stale when its pending version publishes. */
  const [pendingCounts, setPendingCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [chainsLoading, setChainsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const [folderModal, setFolderModal] = useState(false);
  const [docModal, setDocModal] = useState(false);
  /** The document whose filing is being changed, or null. */
  const [moving, setMoving] = useState<LegalDocument | null>(null);
  /** Category filter for the table. null = every category, the default. */
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [f, d] = await Promise.all([documentsApi.listFolders(), documentsApi.listDocuments()]);
      setFolders(f.folders || []);
      // Policies only. An `agreement` is signed via an envelope, not accepted, so it
      // belongs on /legal/agreements. Leaving it here would also make this page ask
      // the acceptance ledger a question that does not apply to it — the answer is a
      // truthful 0, but it is a meaningless column and a wasted request.
      // `listDocuments` has no `kind` filter, so the split is client-side; mirrors
      // AgreementsList.tsx:112.
      const only = (d.documents || []).filter((doc) => doc.kind === 'policy');
      setDocuments(only);
      return only;
    } catch (e: any) {
      setError(e?.message || 'documents_unavailable');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /** Pull each document's chain, then the blast radius of anything in flight. */
  const loadChains = useCallback(async (docs: readonly LegalDocument[]) => {
    if (!docs.length) return;
    setChainsLoading(true);
    try {
      const rows = await mapPool(docs, POOL, async (doc) => {
        try {
          return await documentsApi.getDocument(doc.documentId);
        } catch {
          return null;
        }
      });
      const nextDetails: Record<string, DocumentDetail> = {};
      rows.forEach((r) => { if (r) nextDetails[r.document.documentId] = r; });
      setDetails(nextDetails);

      // Only versions that have left the author's hands can strand an
      // acceptance, so only those are costed. `material` is the question the
      // number answers: "if this ships as a material change, who is stranded?"
      const inFlight = Object.values(nextDetails)
        .map((d) => ({ documentId: d.document.documentId, version: pendingVersion(d.versions) }))
        .filter((x) => !!x.version);

      const counts = await mapPool(inFlight, POOL, async (x) => {
        try {
          const r = await documentsApi.blastRadius(x.documentId, x.version!.versionNo, 'material');
          return { documentId: x.documentId, count: r.staleAcceptanceCount };
        } catch {
          return null;
        }
      });
      const nextCounts: Record<string, number> = {};
      counts.forEach((c) => { if (c) nextCounts[c.documentId] = c.count; });
      setPendingCounts(nextCounts);
    } finally {
      setChainsLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    const docs = await load();
    await loadChains(docs);
  }, [load, loadChains]);

  useEffect(() => { void refresh(); }, [refresh]);

  const tree = useMemo(() => buildTree(folders), [folders]);

  // Policy categories only — this page never offers "Device finance".
  const { options: categoryChoices } = useDocumentCategories('policy');

  const docsInFolder = useMemo(() => {
    const m: Record<string, number> = {};
    documents.forEach((d) => { m[d.folderId] = (m[d.folderId] || 0) + 1; });
    return m;
  }, [documents]);

  // Two independent filters, and they answer different questions: the folder
  // tree is "where is this filed", the category dropdown is "where is this
  // offered". Neither implies the other, so they narrow together.
  const visible = useMemo(() => {
    let list = selectedFolderId ? documents.filter((d) => d.folderId === selectedFolderId) : documents;
    if (categoryFilter) {
      list = categoryFilter === '__none__'
        ? list.filter((d) => !d.category)
        : list.filter((d) => d.category === categoryFilter);
    }
    return list;
  }, [documents, selectedFolderId, categoryFilter]);

  const stats = useMemo(() => {
    const list = Object.values(details);
    const published = list.filter(
      (d) => d.document.status === 'active' && d.activeVersionNo !== null,
    ).length;
    const awaiting = list.filter((d) => headVersion(d.versions)?.status === 'in_review').length;
    const goingStale = Object.values(pendingCounts).reduce((a, b) => a + b, 0);
    return { published, awaiting, goingStale };
  }, [details, pendingCounts]);

  const renderNode = (node: TreeNode, depth: number): JSX.Element => {
    const { folder, children } = node;
    const on = selectedFolderId === folder.folderId;
    const isCollapsed = !!collapsed[folder.folderId];
    return (
      <div key={folder.folderId}>
        <div
          className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg px-2 text-[13px] transition-colors ${
            on ? 'bg-rose-50 font-semibold text-admin-primary' : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
          }`}
          style={{ paddingLeft: 8 + depth * 14 }}
          onClick={() => setSelectedFolderId(folder.folderId)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') setSelectedFolderId(folder.folderId); }}
        >
          {children.length > 0 ? (
            <button
              type="button"
              aria-label={isCollapsed ? 'Expand folder' : 'Collapse folder'}
              onClick={(e) => {
                e.stopPropagation();
                setCollapsed((c) => ({ ...c, [folder.folderId]: !c[folder.folderId] }));
              }}
              className="text-stone-400 hover:text-stone-700"
            >
              {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          ) : (
            <span className="w-3.5" />
          )}
          <span className="truncate">{folder.name}</span>
          <span className="ml-auto text-[10px] text-stone-400">{docsInFolder[folder.folderId] || 0}</span>
        </div>
        {!isCollapsed && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <StaffPage
      section="Legal"
      tabs={<LegalNav />}
      icon={ScrollText}
      title="Policies"
      subtitle="Authored, versioned, approved and published. Every version kept forever."
      actions={(
        <>
          <Button onClick={() => void refresh()} disabled={loading}>
            {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
          <Button onClick={() => setFolderModal(true)}>
            <FolderPlus className="h-4 w-4" />
            New folder
          </Button>
          <Button variant="primary" onClick={() => setDocModal(true)}>
            <Plus className="h-4 w-4" />
            New document
          </Button>
        </>
      )}
    >
      <div className="space-y-5">
        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            Could not reach documents-service: <b>{error}</b>
          </div>
        )}

        {/* Built-in master legal set (additive, 2026-10-09). The library below is unchanged. */}
        <SystemDocumentsPanel kind="policy" documents={documents} loading={loading} detailBase="/legal/policies" />

        <div className="grid gap-3 sm:grid-cols-3">
          <StatTile value={String(stats.published)} label="Published policies" />
          <StatTile value={String(stats.awaiting)} label="Awaiting approval" tone="text-amber-600" />
          <StatTile
            value={String(stats.goingStale)}
            label="Acceptances going stale"
            tone={stats.goingStale > 0 ? 'text-red-600' : 'text-stone-900'}
          />
        </div>

        {/* `items-start` + `h-fit` so the rail is only as tall as its folders,
            and `minmax(0,1fr)` + `min-w-0` on the table card so a wide table
            scrolls inside its own card instead of pushing the whole page
            sideways (a grid track's default min is its content's width). */}
        <div className="grid items-start gap-4 lg:grid-cols-[242px_minmax(0,1fr)]">
          {/* folder tree */}
          <div className="h-fit rounded-xl border border-stone-200 bg-white shadow-sm lg:sticky lg:top-0">
            <h3 className="border-b border-stone-200 px-4 py-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
              Folders
            </h3>
            <div className="p-2">
              <div
                className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg px-2 text-[13px] transition-colors ${
                  selectedFolderId === null
                    ? 'bg-rose-50 font-semibold text-admin-primary'
                    : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                }`}
                onClick={() => setSelectedFolderId(null)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setSelectedFolderId(null); }}
              >
                <span className="w-3.5" />
                <span>All documents</span>
                <span className="ml-auto text-[10px] text-stone-400">{documents.length}</span>
              </div>
              {tree.map((n) => renderNode(n, 0))}
              {folders.length === 0 && !loading && (
                <p className="px-2 py-3 text-[12px] text-stone-500">
                  No folders yet. Create one before adding a document — a document must live in a folder.
                </p>
              )}
            </div>
          </div>

          {/* document table */}
          <div className="min-w-0 rounded-xl border border-stone-200 bg-white shadow-sm">
            {/* Category filter — the other axis. The folder tree beside it says
                where a document is FILED; this says where it is OFFERED. */}
            <div className="flex flex-wrap items-center gap-3 border-b border-stone-200 px-3 py-2.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Category</span>
              <div className="min-w-[240px]">
                <SearchableDropdown
                  options={[
                    { value: '__all__', label: 'All categories' },
                    { value: '__none__', label: 'Uncategorised', subtitle: 'Offered nowhere until a category is set' },
                    ...categoryChoices,
                  ]}
                  value={categoryFilter ?? '__all__'}
                  onChange={(v) => setCategoryFilter(!v || v === '__all__' ? null : v)}
                  placeholder="All categories"
                />
              </div>
              {categoryFilter && (
                <span className="text-[11.5px] text-stone-500">
                  {visible.length} of {documents.length} shown
                </span>
              )}
            </div>
            <div className="overflow-x-auto rounded-b-xl">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-stone-50">
                    {['Document', 'Category', 'Status', 'Active', 'Effective', 'Pending', ''].map((h) => (
                      <th
                        key={h}
                        className="whitespace-nowrap border-b border-stone-200 px-3 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wider text-stone-500"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {loading && (
                    <tr>
                      <td colSpan={7} className="px-3">
                        <RubyLoader variant="section" label="Loading documents…" />
                      </td>
                    </tr>
                  )}
                  {!loading && visible.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-6 py-14">
                        <div className="mx-auto flex max-w-sm flex-col items-center text-center">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-admin-primary">
                            <ScrollText className="h-5 w-5" />
                          </div>
                          <p className="mt-3 text-[14px] font-semibold text-stone-900">
                            {categoryFilter
                              ? 'No policies in this category'
                              : selectedFolderId ? 'This folder is empty' : 'No policies yet'}
                          </p>
                          <p className="mt-1 text-[12.5px] text-stone-500">
                            {categoryFilter
                              ? 'Try another category, or clear the filter to see everything.'
                              : folders.length === 0
                                ? 'A policy must live in a folder — create one first, then add the policy.'
                                : 'Create a policy here. It starts as a v1 draft and nothing is public until you publish.'}
                          </p>
                          <div className="mt-4 flex gap-2">
                            {categoryFilter ? (
                              <Button onClick={() => setCategoryFilter(null)}>Clear filter</Button>
                            ) : folders.length === 0 ? (
                              <Button variant="primary" onClick={() => setFolderModal(true)}>
                                <FolderPlus className="h-4 w-4" />
                                New folder
                              </Button>
                            ) : (
                              <Button variant="primary" onClick={() => setDocModal(true)}>
                                <Plus className="h-4 w-4" />
                                New document
                              </Button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loading && visible.map((doc) => {
                    const detail = details[doc.documentId];
                    const versions = detail?.versions ?? [];
                    const state = detail ? documentHeadState(doc.status, versions) : null;
                    const active = detail ? activeVersionRow(versions, detail.activeVersionNo) : null;
                    const inFlight = detail ? pendingVersion(versions) : null;
                    const count = pendingCounts[doc.documentId];
                    const retired = doc.status === 'retired';
                    return (
                      <tr
                        key={doc.documentId}
                        onClick={() => navigate(`/legal/policies/${encodeURIComponent(doc.documentId)}`)}
                        className="cursor-pointer hover:bg-stone-50"
                      >
                        <td className="px-3 py-3 align-middle">
                          <div className={`flex flex-wrap items-center gap-2 text-[13px] font-medium ${retired ? 'text-stone-400' : 'text-stone-900'}`}>
                            <span>{doc.title}</span>
                            <span className="rounded border border-stone-300 px-1.5 py-0.5 text-[10px] text-stone-500">
                              {doc.kind}
                            </span>
                          </div>
                          <div className="mt-0.5 text-[11px] text-stone-400">/legal/{doc.slug}</div>
                        </td>
                        {/* Where it is OFFERED. Uncategorised is called out rather
                            than left blank — it means the document reaches no
                            picker at all, which is easy to do by accident. */}
                        <td className="px-3 py-3 align-middle">
                          {doc.category
                            ? <span className="text-[12.5px] text-stone-700">{categoryLabel(doc.category)}</span>
                            : <span className="text-[11.5px] italic text-amber-600">Uncategorised</span>}
                        </td>
                        <td className="px-3 py-3 align-middle">
                          {state
                            ? <Tag tone={state.tone}>{state.label}</Tag>
                            : <span className="text-[12px] text-stone-400">…</span>}
                        </td>
                        <td className="px-3 py-3 align-middle text-[13px] text-stone-600">
                          {detail ? (detail.activeVersionNo ? `v${detail.activeVersionNo}` : '—') : '…'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 align-middle text-[13px] text-stone-600">
                          {detail ? formatUtcDay(active?.effectiveAt) : '…'}
                        </td>
                        <td className="px-3 py-3 align-middle text-[13px]">
                          {!inFlight && <span className="text-stone-400">—</span>}
                          {inFlight && count === undefined && (
                            <span className="text-stone-400">{chainsLoading ? '…' : '—'}</span>
                          )}
                          {inFlight && count !== undefined && (
                            <b className={count > 0 ? 'text-red-600' : 'text-stone-500'}>{count}</b>
                          )}
                        </td>
                        {/* `stopPropagation` — the row itself opens the document,
                            and moving is a different intent entirely. */}
                        <td className="whitespace-nowrap px-3 py-3 text-right align-middle">
                          <button
                            type="button"
                            title="Move to another folder or category"
                            aria-label={`Move ${doc.title}`}
                            onClick={(e) => { e.stopPropagation(); setMoving(doc); }}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-admin-primary"
                          >
                            <FolderInput size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Explainer summary={(
          <>
            <b>0 means nobody owes re-acceptance.</b> A dash means the number could not be
            computed.
          </>
        )}
        >
          Figures are computed live and never stored. Somebody owes re-acceptance only when a{' '}
          <b>material</b> version took effect after the one they accepted — a run of minor
          changes never forces anyone to re-accept.
        </Explainer>
      </div>

      {moving && (
        <MoveDocumentModal
          document={moving}
          folders={folders}
          kind="policy"
          onClose={() => setMoving(null)}
          onMoved={() => { setMoving(null); void refresh(); }}
        />
      )}

      {folderModal && (
        <NewFolderModal
          folders={folders}
          onClose={() => setFolderModal(false)}
          onCreated={() => { setFolderModal(false); void refresh(); }}
        />
      )}
      {docModal && (
        <NewDocumentModal
          folders={folders}
          defaultFolderId={selectedFolderId}
          onClose={() => setDocModal(false)}
          onCreated={(documentId) => {
            setDocModal(false);
            navigate(`/legal/policies/${encodeURIComponent(documentId)}`);
          }}
        />
      )}
    </StaffPage>
  );
}

/* ── new folder ─────────────────────────────────────────────────────────── */

function NewFolderModal({
  folders, onClose, onCreated,
}: { folders: readonly Folder[]; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [parentFolderId, setParentFolderId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) { setError('A folder needs a name.'); return; }
    setSaving(true);
    setError(null);
    try {
      await documentsApi.createFolder({
        name: name.trim(),
        parentFolderId: parentFolderId || undefined,
      });
      toastService.success('Folder created');
      onCreated();
    } catch (e: any) {
      setError(e?.message || 'folder_create_failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title="New folder"
      subtitle="Folders organise documents. They do not affect a document's public URL."
      onClose={onClose}
      footerNote="Folders can be renamed or moved later."
      footerActions={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={saving || !name.trim()}>
            {saving ? 'Creating…' : 'Create folder'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <FieldLabel requirement="required">Name</FieldLabel>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Legal &amp; Privacy"
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <FieldLabel>Parent folder</FieldLabel>
          <SearchableDropdown
            options={folders.map((f) => ({ value: f.folderId, label: f.name }))}
            value={parentFolderId}
            onChange={setParentFolderId}
            placeholder="Top level"
          />
        </div>
        {error && (
          <p className="rounded-lg border border-red-300 bg-red-50 px-3.5 py-3 text-[12px] text-red-700">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}

/* ── new document ───────────────────────────────────────────────────────── */

const KIND_OPTIONS: { value: DocumentKind; label: string; subtitle: string }[] = [
  { value: 'policy', label: 'Policy', subtitle: 'Accepted by a party' },
  { value: 'agreement', label: 'Agreement', subtitle: 'Instantiated and signed (Stage C)' },
];

function NewDocumentModal({
  folders, defaultFolderId, onClose, onCreated,
}: {
  folders: readonly Folder[];
  defaultFolderId: string | null;
  onClose: () => void;
  onCreated: (documentId: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [kind, setKind] = useState<DocumentKind>('policy');
  const [folderId, setFolderId] = useState<string | null>(defaultFolderId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(title);

  const problems: string[] = [];
  if (!title.trim()) problems.push('Give the document a title.');
  if (!folderId) problems.push('Choose a folder.');
  if (!effectiveSlug || !isValidSlug(effectiveSlug)) {
    problems.push('The slug must be lowercase words separated by single hyphens.');
  }

  /**
   * WHERE this document is offered. Optional at the API, asked here: an
   * uncategorised document appears in no picker, so leaving it blank is a way
   * to lose a document rather than a neutral choice.
   */
  const [category, setCategory] = useState<DocumentCategory | null>(null);
  // Live categories for this kind — the same list the table filters by, so a
  // category added at /legal/categories is offered here without a deploy.
  const { options: modalCategoryChoices } = useDocumentCategories('policy');

  const submit = async () => {
    if (problems.length) return;
    setSaving(true);
    setError(null);
    try {
      const res = await documentsApi.createDocument({
        folderId: folderId!, slug: effectiveSlug, title: title.trim(), kind,
      });
      toastService.success(`${res.document.title} created as v1 draft`);
      onCreated(res.document.documentId);
    } catch (e: any) {
      setError(
        e?.message === 'slug_already_exists'
          ? 'That slug is already taken. Slugs are permanent public URLs and are never reused, '
            + 'even after a document is retired.'
          : e?.message || 'document_create_failed',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title="New document"
      subtitle="Created with an empty v1 draft. Nothing is public until a version is published."
      onClose={onClose}
      footerNote="The slug is permanent — it can never be changed or reused."
      footerActions={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={saving || problems.length > 0}
          >
            {saving ? 'Creating…' : 'Create document'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <FieldLabel requirement="required">Title</FieldLabel>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Privacy Policy"
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <FieldLabel requirement="required · permanent">Slug</FieldLabel>
          <input
            value={effectiveSlug}
            onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }}
            placeholder="privacy-policy"
            className={INPUT_CLASS}
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            Public URL: <span className="text-stone-700">/legal/{effectiveSlug || '…'}</span>
          </p>
        </div>

        <div>
          <FieldLabel requirement="required">Folder</FieldLabel>
          <SearchableDropdown
            options={folders.map((f) => ({ value: f.folderId, label: f.name }))}
            value={folderId}
            onChange={setFolderId}
            placeholder={folders.length ? 'Choose a folder' : 'Create a folder first'}
            disabled={folders.length === 0}
          />
        </div>

        <div>
          <FieldLabel requirement="recommended">Category</FieldLabel>
          <SearchableDropdown
            options={modalCategoryChoices}
            value={category}
            onChange={(value) => setCategory(value as DocumentCategory | null)}
            placeholder="Where is this offered?"
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            Decides where this appears — a device rental agreement is offered on the rent option, a software
            licence at checkout. Without one it stays in the library and shows up in no picker.
          </p>
        </div>

        <div>
          <FieldLabel requirement="required">Kind</FieldLabel>
          <SearchableDropdown
            options={KIND_OPTIONS}
            value={kind}
            onChange={(v) => setKind((v as DocumentKind) || 'policy')}
            placeholder="Policy"
          />
        </div>

        {problems.length > 0 && (title || slugTouched || folderId) && (
          <ul className="space-y-1 rounded-lg border border-red-300 bg-red-50 px-3.5 py-3 text-[12px] text-red-700">
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        )}
        {error && (
          <p className="rounded-lg border border-red-300 bg-red-50 px-3.5 py-3 text-[12px] text-red-700">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
